<template>
  <v-dialog
    v-model="dialog"
    max-width="1200px"
    persistent
    scrollable
  >
    <v-card class="elevation-2">
      <v-card-title class="text-h5 font-weight-bold primary--text pa-6">
        {{ $t('aiwf_sb_start_title') }}
        <v-spacer></v-spacer>
        <v-btn
          icon
          @click="closeModal"
          :disabled="loading"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      
      <v-divider></v-divider>
      
      <v-card-text class="pa-6" style="max-height: 70vh;">
        <v-row>
          <!-- Left Column -->
          <v-col cols="12" md="6" class="pr-md-4">
            <div class="mb-6">
              <v-text-field
                v-model="formData.filmTitle"
                :label="$t('aiwf_sb_start_film_title') + ' *'"
                outlined
                dense
                :rules="[rules.required]"
                class="mb-4"
              ></v-text-field>
              
              <v-textarea
                v-model="formData.brandDescription"
                :label="$t('aiwf_sb_start_brand_description') + ' *'"
                outlined
                rows="3"
                :rules="[rules.required]"
                class="mb-4"
              ></v-textarea>
              
              <v-textarea
                v-model="formData.scriptStyle"
                :label="$t('aiwf_sb_start_script_style')"
                outlined
                rows="3"
                class="mb-4"
              ></v-textarea>
              
              <div class="mb-4">
                <v-file-input
                  v-model="formData.imageReference"
                  :label="$t('aiwf_sb_start_upload_image')"
                  outlined
                  dense
                  accept="image/*"
                  prepend-icon=""
                  prepend-inner-icon="mdi-paperclip"
                  show-size
                  :rules="[rules.fileSize]"
                  @change="handleFileChange"
                ></v-file-input>
              </div>
              
              <v-textarea
                v-model="formData.pointsOfAttention"
                :label="$t('aiwf_sb_start_points_attention')"
                outlined
                rows="3"
              ></v-textarea>
            </div>
          </v-col>
          
          <!-- Right Column -->
          <v-col cols="12" md="6" class="pl-md-4">
            <div class="mb-6">
              <v-text-field
                v-model="formData.product"
                :label="$t('aiwf_sb_start_product') + ' *'"
                outlined
                dense
                :rules="[rules.required]"
                class="mb-4"
              ></v-text-field>
              
              <v-textarea
                v-model="formData.audienceDescription"
                :label="$t('aiwf_sb_start_audience_description') + ' *'"
                outlined
                rows="3"
                :rules="[rules.required]"
                class="mb-4"
              ></v-textarea>
              
              <v-textarea
                v-model="formData.visualMood"
                :label="$t('aiwf_sb_start_visual_mood')"
                outlined
                rows="3"
                class="mb-4"
              ></v-textarea>
              
              <v-row>
                <v-col cols="6">
                  <v-select
                    v-model="formData.dimension"
                    :items="dimensionOptions"
                    :label="$t('aiwf_sb_start_dimension') + ' *'"
                    outlined
                    dense
                    :rules="[rules.required]"
                  ></v-select>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="formData.totalDuration"
                    :label="$t('aiwf_sb_start_total_duration') + ' *'"
                    type="number"
                    min="1"
                    outlined
                    dense
                    :rules="[rules.required, rules.positiveNumber]"
                    suffix="seconds"
                  ></v-text-field>
                </v-col>
              </v-row>
              
              <v-textarea
                v-model="formData.otherInstructions"
                :label="$t('aiwf_sb_start_other_instructions')"
                outlined
                rows="3"
              ></v-textarea>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      
      <v-divider></v-divider>
      
      <v-card-actions class="pa-6 d-flex justify-space-between align-center">
        <div class="required-fields-text">
          * required fields
        </div>
        
        <div>
          <v-btn
            color="grey"
            text
            large
            @click="closeModal"
            :disabled="loading"
            class="mr-3"
          >
            {{ $t('cancel') }}
          </v-btn>
          
          <v-btn
            color="success"
            large
            @click="handleSubmit"
            :loading="loading"
            :disabled="!isFormValid"
          >
            {{ $t('aiwf_sb_start_create') }}
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions} from 'vuex'
import AppActions from '@/store/app/actions-types'

export default {
  name: 'NewStoryboardForm',
  
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      formData: {
        filmTitle: '',
        brandDescription: '',
        scriptStyle: '',
        imageReference: null,
        pointsOfAttention: '',
        product: '',
        audienceDescription: '',
        visualMood: '',
        dimension: '',
        totalDuration: null,
        otherInstructions: ''
      },
      loading: false,
      dimensionOptions: [
        { text: '16:9 (Landscape)', value: '16:9' },
        { text: '9:16 (Portrait)', value: '9:16' },
      ],
      rules: {
        required: value => !!value || this.$t('field_required'),
        positiveNumber: value => (value && value > 0) || this.$t('form_rules.positive_number'),
        fileSize: value => {
          if (!value) return true
          return value.size < 10000000 || this.$t('file_too_large')
        }
      }
    }
  },

  computed: {
    dialog: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },

    isFormValid() {
      const requiredFields = [
        'filmTitle',
        'brandDescription', 
        'product',
        'audienceDescription',
        'dimension',
        'totalDuration'
      ]
      
      return requiredFields.every(field => !!this.formData[field]) && 
             this.formData.totalDuration > 0
    }
  },

  methods: {
    ...mapActions('app', [
      AppActions.OPEN_APP_INFO_MESSAGE,
      AppActions.OPEN_APP_SUCCESS_MESSAGE,
      AppActions.OPEN_APP_ERROR_MESSAGE
    ]),

    closeModal() {
      this.resetForm()
      this.$emit('input', false)
      this.$emit('close')
    },

    resetForm() {
      this.formData = {
        filmTitle: '',
        brandDescription: '',
        scriptStyle: '',
        imageReference: null,
        pointsOfAttention: '',
        product: '',
        audienceDescription: '',
        visualMood: '',
        dimension: '',
        totalDuration: null,
        otherInstructions: ''
      }
    },

    handleFileChange(file) {
      this.formData.imageReference = file
    },

    handleSubmit() {
      if (!this.isFormValid) {
        this[AppActions.OPEN_APP_INFO_MESSAGE](this.$t('aiwf_sb_start_fill_required_fields'))
        return
      }

      this.loading = true
      this.$emit('submit', {
        formData: { ...this.formData },
        formattedText: this.createFormattedText()
      })
    },

    createFormattedText() {
      const parts = []
      
      parts.push(`Olá, eu quero que você me ajude a criar um storyboard para um filme.`)

      // Film Title
      if (this.formData.filmTitle) {
        parts.push(`O título do filme é ${this.formData.filmTitle}.`)
      }
      
      // Product
      if (this.formData.product) {
        parts.push(`O produto é ${this.formData.product}.`)
      }
      
      // Brand Description
      if (this.formData.brandDescription) {
        parts.push(`Descrição da marca é ${this.formData.brandDescription}.`)
      }
      
      // Audience Description
      if (this.formData.audienceDescription) {
        parts.push(`Descrição do público é ${this.formData.audienceDescription}.`)
      }
      
      // Dimension
      if (this.formData.dimension) {
        parts.push(`Dimensão é ${this.formData.dimension}.`)
      }
      
      // Total Duration
      if (this.formData.totalDuration) {
        parts.push(`A duração total é ${this.formData.totalDuration} segundos.`)
      }
      
      // Script Style (optional field)
      if (this.formData.scriptStyle) {
        parts.push(`O estilo do script é ${this.formData.scriptStyle}.`)
      }
      
      // Visual Mood (optional field)
      if (this.formData.visualMood) {
        parts.push(`O humor visual é ${this.formData.visualMood}.`)
      }
      
      // Points of Attention (optional field)
      if (this.formData.pointsOfAttention) {
        parts.push(`Pontos de Atenção é ${this.formData.pointsOfAttention}.`)
      }
      
      // Other Instructions (optional field)
      if (this.formData.otherInstructions) {
        parts.push(`Outras instruções são ${this.formData.otherInstructions}.`)
      }

      parts.push(`Não se esqueça de chamar a ferramenta de criar a pasta raiz.`)
      parts.push(`Mostre-me o json do storyboard.`)
      
      return parts.join(' ')
    },

    // Public method to set loading state from parent
    setLoading(loading) {
      this.loading = loading
    }
  },

  watch: {
    value(newVal) {
      if (newVal) {
        this.resetForm()
      }
    }
  }
}
</script>

<style scoped>
/* Modal styles */
.v-card {
  border-radius: 12px;
}

.v-card-title {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.v-textarea >>> .v-input__control .v-input__slot {
  min-height: 80px;
}

.required-fields-text {
  color: red;
  font-size: 12px;
}

@media (max-width: 800px) {
  .pr-md-4 {
    padding-right: 12px !important;
  }
  
  .pl-md-4 {
    padding-left: 12px !important;
  }
}
</style>
